import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, X } from 'lucide-react';
import { customerApi, GeoAutomationScene, GeoAutomationPOI } from '../services/customerApi';
import { Viewer } from '@ydrive/gs-viewer';
import POISidePanel from '../components/POISidePanel';
import { wgs84ToLocal } from '../utils/poiUtils';
import { walkToPOIWithCamera } from '../utils/walkToPOIWithCamera';

import { fetchManifest, findSegmentForPOI, ManifestData, localToWGS84 } from '../utils/manifestUtils';

type AxisMapping = 'xyz' | 'xzy' | 'yxz' | 'yzx' | 'zxy' | 'zyx' | 'x-yz' | 'xz-y' | 'x-z-y' | '-xyz' | '-xzy';

const logo = '/logo.gif';

const getGeoCoordinatesForScene = (sceneId: string) => {
  switch (sceneId.toLowerCase()) {      
    case 'terrebonne':
      return {
        latitude: 45.71491151,   // Segment 6 center (3 POIs - most in scene)
        longitude: -73.64610261,
        altitude: 58.0,          // Eye level
        heading: 0
      };
    case 'officespace':
      return {
        latitude: 45.5017,
        longitude: -73.5673,
        altitude: 2,
        heading: 0
      };
    default:
      return {
        latitude: 0,
        longitude: 0,
        altitude: 0,
        heading: 0
      };
  }
};

const GeoAutomationWithPOIViewer: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const geoScene = searchParams.get('scene');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [scene, setScene] = useState<GeoAutomationScene | undefined>(undefined);
  const [showNavPanel, setShowNavPanel] = useState(false);
  
  const [pathNetwork, setPathNetwork] = useState<number[][][]>([]);

  // POI state
  const [pois, setPois] = useState<GeoAutomationPOI[]>([]);
  const [filteredPois, setFilteredPois] = useState<GeoAutomationPOI[]>([]);
  const [loadingPois, setLoadingPois] = useState(false);
  const [selectedPOI, setSelectedPOI] = useState<GeoAutomationPOI | null>(null);

  const [axisMapping, setAxisMapping] = useState<AxisMapping>('xzy');
  const [showAxisDebug, setShowAxisDebug] = useState(false);
  
  // Manifest state
  const [manifest, setManifest] = useState<ManifestData | null>(null);
  const [currentSegment, setCurrentSegment] = useState<number | null>(null);


  
  // Camera position
  const [cameraPosition, setCameraPosition] = useState<{
    latitude: number;
    longitude: number;
    altitude: number;
    heading: number;
  } | null>(null);
  
  const viewerRef = useRef<any>(null);
  const walkthroughCancelRef = useRef<(() => void) | null>(null);
 
  // Fetch scene data
  useEffect(() => {
    let isActive = true;
    const fetchAndLoadScene = async () => {
      try {
        setLoading(true);
        setError(null);

        if (!geoScene) {
          throw new Error('Missing required URL parameter: scene');
        }

        console.log('[GeoViewer] Fetching scene for geoScene:', geoScene);
        const response = await customerApi.getGeoAutomationSceneBySceneId(geoScene);
        if (!isActive) return;

        if (response.success && response.data) {
          const sceneData = response.data;
          console.log('[GeoViewer] Loaded scene:', sceneData.sceneName);
          
          if (!sceneData.manifest) {
            throw new Error(`Scene '${sceneData.sceneName}' does not have a manifest URL`);
          }
          
          // Fetch manifest for segment matching
          try {
            const manifestData = await fetchManifest(sceneData.manifest);
            setManifest(manifestData);
            console.log('[GeoViewer] Manifest loaded with', manifestData.segment_count, 'segments');
          } catch (err) {
            console.warn('[GeoViewer] Could not load manifest:', err);
          }
          
          setScene(sceneData);
          loadScene(sceneData, () => isActive);
        } else {
          throw new Error(`No scene found for scene ID: ${geoScene}`);
        }
      } catch (err) {
        console.error('[GeoViewer] Error fetching scene:', err);
        if (isActive) {
          setError(err instanceof Error ? err.message : 'Failed to load scene');
          setLoading(false);
        }
      }
    };

    fetchAndLoadScene();
    
    return () => {
      isActive = false;
      // Cancel any ongoing walkthrough
      if (walkthroughCancelRef.current) {
        walkthroughCancelRef.current();
      }
      // Dispose viewer on unmount
      if (viewerRef.current && typeof viewerRef.current.dispose === 'function') {
        try {
          console.log('[GeoViewer] Disposing viewer on unmount');
          viewerRef.current.dispose();
          viewerRef.current = null;
        } catch (error) {
          console.warn('[GeoViewer] Error disposing on unmount:', error);
        }
      }
    };
  }, [geoScene]);

  // Fetch ALL POI types
  useEffect(() => {
    const fetchPOIs = async () => {
      if (!geoScene) return;

      try {
        setLoadingPois(true);
        
        
        const response = await customerApi.getGeoAutomationPOIs(geoScene);
        
        if (response.success) {
          
          setPois(response.data);
          
          const breakdown = response.data.reduce((acc, poi) => {
            acc[poi.type] = (acc[poi.type] || 0) + 1;
            return acc;
          }, {} as Record<string, number>);
          
        }
      } catch (err) {
        console.error('[GeoViewer] Error fetching POIs:', err);
      } finally {
        setLoadingPois(false);
      }
    };

    if (scene && !loading) {
      fetchPOIs();
    }
  }, [scene, loading, geoScene]);



  // Track camera position and current segment
  // Track camera position and current segment
useEffect(() => {
  const updateCameraPosition = () => {
    try {
      // FIRST: Try to get REAL camera position from viewer
      if (viewerRef.current?.camera && manifest) {
        const camera = viewerRef.current.camera;
        const origin = manifest.observer_coordinates;
        
        // Get camera position in local coords (yzx mapping)
        const localX = camera.position.z;
        const localY = camera.position.x;
        const localZ = camera.position.y;
        
        // Convert to WGS84
        const wgs84 = localToWGS84(localX, localY, localZ, origin);
        
        const position = {
          latitude: wgs84.latitude,
          longitude: wgs84.longitude,
          altitude: wgs84.altitude,
          heading: 0
        };
        
        setCameraPosition(position);
        
        // Update segment (no logging!)
        const segment = findSegmentForPOI(
          position.latitude,
          position.longitude,
          position.altitude,
          manifest
        );
        
        if (segment !== currentSegment) {
          setCurrentSegment(segment);
          // Only log segment changes (not every frame)
          console.log('[GeoViewer] Segment:', segment);
        }
        
        return;
      }
      
      // FALLBACK: Use URL params if camera not ready yet
      const latParam = searchParams.get('lat');
      const lonParam = searchParams.get('lon');
      const altParam = searchParams.get('alt');
      const headingParam = searchParams.get('heading');
      
      let position;
      
      if (latParam && lonParam) {
        position = {
          latitude: parseFloat(latParam),
          longitude: parseFloat(lonParam),
          altitude: altParam ? parseFloat(altParam) : 1.5,
          heading: headingParam ? parseFloat(headingParam) : 0
        };
      } else if (geoScene) {
        const defaultCoords = getGeoCoordinatesForScene(geoScene);
        position = {
          latitude: defaultCoords.latitude,
          longitude: defaultCoords.longitude,
          altitude: defaultCoords.altitude,
          heading: defaultCoords.heading
        };
      }
      
      if (position) {
        setCameraPosition(position);
      }
      
    } catch (error) {
      // Silent fail - don't spam console
    }
  };

  updateCameraPosition();
  const interval = setInterval(updateCameraPosition, 1000);  // 1000ms = 1 second
  return () => clearInterval(interval);
}, [manifest]);  // ONLY manifest dependency!

  // Filter POIs by current segment (or nearby segments)
  useEffect(() => {
    if (!manifest || !cameraPosition) {
      setFilteredPois(pois);
      return;
    }

    //get POIs within same segment only 
    const nearbyPOIs = pois.filter(poi => {
  const poiSegment = findSegmentForPOI(
    poi.latitude,
    poi.longitude,
    poi.altitude,
    manifest
  );
  
  // ONLY same segment - no distance fallback
  return poiSegment === currentSegment;
});

     setFilteredPois(nearbyPOIs);
  }, [pois, currentSegment, manifest, cameraPosition]);

  useEffect(() => {
  if (!scene) return;

  // Load path network based on scene
  const loadPathNetwork = async () => {
    try {
      let pathUrl = '';
      
      switch (scene.sceneId.toLowerCase()) {
        case 'terrebonne':
          pathUrl = '/path-networks/terrebonne_path_8km.json';
          break;
        // Add other scenes as needed
      }

      if (pathUrl) {
        const response = await fetch(pathUrl);
        const pathData = await response.json();
        setPathNetwork(pathData);
        
      }
    } catch (error) {
      console.error('[GeoViewer] Error loading path network:', error);
    }
  };

  loadPathNetwork();
  return () => {
    setPathNetwork([]);  // Clear on unmount
  };
}, [scene]);

  // Load 3D scene 
  const loadScene = (sceneToLoad: GeoAutomationScene, checkIsActive: () => boolean) => {
    try {
      
      
      if (viewerRef.current && viewerRef.current !== 'cleaning') {
        try {
          viewerRef.current.dispose();
        } catch (error) {
          console.warn('[GeoViewer] Error disposing:', error);
        }
        viewerRef.current = null;
      }
      
      const viewerOptions: any = {
        sharedMemoryForWorkers: false,
        cameraFar: 100000,
        disableSplatTree: false,
        cameraFoV: 60
      };

      const viewer = new Viewer(viewerOptions) as any;
      viewerRef.current = viewer;

      const latParam = searchParams.get('lat');
      const lonParam = searchParams.get('lon');
      const altParam = searchParams.get('alt');
      const headingParam = searchParams.get('heading');
      
      let geoCoords;
      const CAR_EYE_LEVEL = 1.5;
      
      if (latParam && lonParam) {
        geoCoords = {
          latitude: parseFloat(latParam),
          longitude: parseFloat(lonParam),
          altitude: altParam ? parseFloat(altParam) : CAR_EYE_LEVEL,
          heading: headingParam ? parseFloat(headingParam) : 0
        };
      } else {
        geoCoords = getGeoCoordinatesForScene(sceneToLoad.sceneId);
        if (altParam) geoCoords.altitude = parseFloat(altParam);
        if (headingParam) geoCoords.heading = parseFloat(headingParam);
      }
      
      const sceneOptions: any = {
        showLoadingUI: false,
        latitude: geoCoords.latitude,
        longitude: geoCoords.longitude,
        altitude: geoCoords.altitude,
        heading: geoCoords.heading  
      };

      viewer.addSplatScenesFromManifest(sceneToLoad.manifest, sceneOptions)
      .then((result: any) => {
        if (!checkIsActive()) return;
        
        viewer.start();
        setLoading(false);
        
        setTimeout(() => {
          if (!checkIsActive()) return;
          setShowNavPanel(true);
        }, 3000);       
      })
      .catch((err: any) => {
        if (!checkIsActive()) return;
        console.error('[GeoViewer] Error:', err);
        setError('Failed to load 3D scene');
        setLoading(false);
      });
      
    } catch (error) {
      console.error('[GeoViewer] Error in loadScene:', error);
      if (checkIsActive()) {
        setError('Failed to initialize viewer');
        setLoading(false);
      }
    }
  };



  /* Handle POI navigation with camera walkthrough (NO RELOAD!)
const handleNavigateToPOI = (poi: GeoAutomationPOI) => {
  console.log('[GeoViewer] Walking to POI:', poi.name);
  
  if (!cameraPosition || !manifest || !viewerRef.current) {
    console.warn('[GeoViewer] Cannot walk - missing requirements');
    return;
  }

  // Cancel any ongoing walkthrough
  if (walkthroughCancelRef.current) {
    walkthroughCancelRef.current();
  }
  const startLat = cameraPosition.latitude;
  const startLon = cameraPosition.longitude;
  const startAlt = cameraPosition.altitude;

  // Start smooth camera walkthrough
   const cancelWalk = walkToPOIWithCamera(
    viewerRef.current,
    cameraPosition,
    poi,
    manifest,
    {
      duration: 4000,
      offsetDistance: 5,
      onProgress: (progress) => {
        // Use captured values (not stale cameraPosition!)
        const lat = startLat + (poi.latitude - startLat) * progress;
        const lon = startLon + (poi.longitude - startLon) * progress;
        const alt = startAlt + (poi.altitude - startAlt) * progress;
        
        setCameraPosition({
          latitude: lat,
          longitude: lon,
          altitude: alt,
          heading: cameraPosition.heading || 0
        });
        
        if (progress % 0.1 < 0.01) {
          console.log(`[GeoViewer] Walk progress: ${Math.floor(progress * 100)}%`);
        }
      },
      onComplete: () => {
        console.log('[GeoViewer] Arrived at POI:', poi.name);
        
        // ========================================
        // CRITICAL: Update camera position state!
        // ========================================
        const newCameraPosition = {
          latitude: poi.latitude,
          longitude: poi.longitude,
          altitude: poi.altitude,
          heading: cameraPosition.heading || 0
        };
        
        setCameraPosition(newCameraPosition);
        console.log('[GeoViewer] Updated camera position state:', newCameraPosition);
        
        setSelectedPOI(poi);
        
        // Update URL params
        const newParams = new URLSearchParams(searchParams);
        newParams.set('lat', poi.latitude.toString());
        newParams.set('lon', poi.longitude.toString());
        newParams.set('alt', poi.altitude.toString());
        window.history.replaceState({}, '', `?${newParams.toString()}`);
        
        walkthroughCancelRef.current = null;
      }
    }
  );

  walkthroughCancelRef.current = cancelWalk;
};*/

const handleNavigateToPOI = (poi: GeoAutomationPOI) => {
    
  if (!cameraPosition || !manifest || !viewerRef.current) {
    console.warn('❌ [DEBUG] Missing requirements:', {
      hasCameraPosition: !!cameraPosition,
      hasManifest: !!manifest,
      hasViewer: !!viewerRef.current
    });
    return;
  }

  // Cancel any ongoing walkthrough
  if (walkthroughCancelRef.current) {
    
    walkthroughCancelRef.current();
  }

  // Capture start values
  const startLat = cameraPosition.latitude;
  const startLon = cameraPosition.longitude;
  const startAlt = cameraPosition.altitude;
  const startHeading = cameraPosition.heading || 0;

 
  // Start smooth camera walkthrough
  const cancelWalk = walkToPOIWithCamera(
    viewerRef.current,
    cameraPosition,
    poi,
    manifest,
    {
      duration: 4000,
      offsetDistance: 5,
      onProgress: (progress) => {
    
        
        // Interpolate position
        const lat = startLat + (poi.latitude - startLat) * progress;
        const lon = startLon + (poi.longitude - startLon) * progress;
        const alt = startAlt + (poi.altitude - startAlt) * progress;
      
      
        
        setCameraPosition({
          latitude: lat,
          longitude: lon,
          altitude: alt,
          heading: startHeading
        });
        
       
      },
      onComplete: () => {
        
        
        setCameraPosition({
          latitude: poi.latitude,
          longitude: poi.longitude,
          altitude: poi.altitude,
          heading: startHeading
        });
        
        setSelectedPOI(poi);
        
        // Update URL params
        const newParams = new URLSearchParams(searchParams);
        newParams.set('lat', poi.latitude.toString());
        newParams.set('lon', poi.longitude.toString());
        newParams.set('alt', poi.altitude.toString());
        window.history.replaceState({}, '', `?${newParams.toString()}`);
        
        walkthroughCancelRef.current = null;
      
      }
    }
  );

  walkthroughCancelRef.current = cancelWalk;
  
};

  // test mapping temp for testing different axis orders and signs when setting camera.position directly

const testAxisMapping = (mapping: AxisMapping) => {
  if (!viewerRef.current || !cameraPosition || !manifest || filteredPois.length === 0) {
    alert('Wait for scene to load first!');
    return;
  }

  
  
  const camera = viewerRef.current.camera;
  const poi = filteredPois[0]; // Test with first nearby POI
  
  const origin = manifest.observer_coordinates;
  
 
 
  // Convert to local coordinates
  const startLocal = wgs84ToLocal(
    cameraPosition.latitude,
    cameraPosition.longitude,
    cameraPosition.altitude,
    origin
  );
  
  const poiLocal = wgs84ToLocal(
    poi.latitude,
    poi.longitude,
    poi.altitude,
    origin
  );
  
  // ========================================
  // CALCULATE OFFSET POSITION (behind POI)
  // ========================================
  
  // Direction from current position to POI
  const dx = poiLocal.x - startLocal.x;
  const dy = poiLocal.y - startLocal.y;
  const distance = Math.sqrt(dx * dx + dy * dy);
  
  // Normalize direction
  const dirX = dx / distance;
  const dirY = dy / distance;
  
  // Offset distance (5 meters behind POI)
  const offsetDistance = 5;
  
  // Camera position: POI position - (direction * offset)
  const cameraLocal = {
    x: poiLocal.x + (dirX * offsetDistance),
    y: poiLocal.y + (dirY * offsetDistance),
    z: poiLocal.z // Keep same height
  };
  

  
  // Apply the mapping to OFFSET position
  const { x, y, z } = cameraLocal;
  
  
  
  switch (mapping) {
    case 'xyz':
      camera.position.set(x, y, z);
      console.log(`   → camera.position.set(${x.toFixed(2)}, ${y.toFixed(2)}, ${z.toFixed(2)})`);
      break;
    case 'xzy':
      camera.position.set(x, z, y);
      console.log(`   → camera.position.set(${x.toFixed(2)}, ${z.toFixed(2)}, ${y.toFixed(2)})`);
      break;
    case 'yxz':
      camera.position.set(y, x, z);
      console.log(`   → camera.position.set(${y.toFixed(2)}, ${x.toFixed(2)}, ${z.toFixed(2)})`);
      break;
    case 'yzx':
      camera.position.set(y, z, x);
      console.log(`   → camera.position.set(${y.toFixed(2)}, ${z.toFixed(2)}, ${x.toFixed(2)})`);
      break;
    case 'zxy':
      camera.position.set(z, x, y);
      console.log(`   → camera.position.set(${z.toFixed(2)}, ${x.toFixed(2)}, ${y.toFixed(2)})`);
      break;
    case 'zyx':
      camera.position.set(z, y, x);
      console.log(`   → camera.position.set(${z.toFixed(2)}, ${y.toFixed(2)}, ${x.toFixed(2)})`);
      break;
    case 'x-yz':
      camera.position.set(x, -y, z);
      console.log(`   → camera.position.set(${x.toFixed(2)}, ${(-y).toFixed(2)}, ${z.toFixed(2)})`);
      break;
    case 'xz-y':
      camera.position.set(x, z, -y);
      console.log(`   → camera.position.set(${x.toFixed(2)}, ${z.toFixed(2)}, ${(-y).toFixed(2)})`);
      break;
    case 'x-z-y':
      camera.position.set(x, -z, -y);
      console.log(`   → camera.position.set(${x.toFixed(2)}, ${(-z).toFixed(2)}, ${(-y).toFixed(2)})`);
      break;
    case '-xyz':
      camera.position.set(-x, y, z);
      console.log(`   → camera.position.set(${(-x).toFixed(2)}, ${y.toFixed(2)}, ${z.toFixed(2)})`);
      break;
    case '-xzy':
      camera.position.set(-x, z, y);
      console.log(`   → camera.position.set(${(-x).toFixed(2)}, ${z.toFixed(2)}, ${y.toFixed(2)})`);
      break;
  }
  
 
  
  // Make camera LOOK AT the POI (based on mapping)
  // We need to apply the same mapping to the POI position for lookAt
 
  
  const poiX = poiLocal.x;
  const poiY = poiLocal.y;
  const poiZ = poiLocal.z;
  
  // Apply same mapping to lookAt target
  switch (mapping) {
    case 'xyz':
      camera.lookAt(poiX, poiY, poiZ);
      break;
    case 'xzy':
      camera.lookAt(poiX, poiZ, poiY);
      break;
    case 'yxz':
      camera.lookAt(poiY, poiX, poiZ);
      break;
    case 'yzx':
      camera.lookAt(poiY, poiZ, poiX);
      break;
    case 'zxy':
      camera.lookAt(poiZ, poiX, poiY);
      break;
    case 'zyx':
      camera.lookAt(poiZ, poiY, poiX);
      break;
    case 'x-yz':
      camera.lookAt(poiX, -poiY, poiZ);
      break;
    case 'xz-y':
      camera.lookAt(poiX, poiZ, -poiY);
      break;
    case 'x-z-y':
      camera.lookAt(poiX, -poiZ, -poiY);
      break;
    case '-xyz':
      camera.lookAt(-poiX, poiY, poiZ);
      break;
    case '-xzy':
      camera.lookAt(-poiX, poiZ, poiY);
      break;
  }
  
  // Update controls
  if (viewerRef.current.controls?.update) {
    viewerRef.current.controls.update(0);
  }
  
  console.log('✅ Test complete! You should be 5m behind POI, looking at it.\n');
};

  return (
    <>
      {/* Loading Screen */}
      {loading && (
        <div className="fixed inset-0 flex items-center justify-center bg-black z-50">
          <div className="flex flex-col items-center gap-6">
            <img src={logo} alt="Loading..." className="w-20 h-20" />
            <div className="flex items-center gap-3">
              <Loader2 className="h-6 w-6 animate-spin text-white" />
              <p className="text-white text-lg">Loading 3D Scene...</p>
            </div>
          </div>
        </div>
      )}

      {/* Error Screen */}
      {error && (
        <div className="fixed inset-0 flex items-center justify-center bg-black z-50">
          <div className="max-w-md p-8 bg-red-900/20 border border-red-500/30 rounded-lg">
            <h2 className="text-xl font-semibold text-red-400 mb-4">Error Loading Scene</h2>
            <p className="text-red-200 mb-6">{error}</p>
            <button
              onClick={() => navigate('/the-gallery')}
              className="px-6 py-2 bg-red-600 hover:bg-red-700 text-white rounded transition-colors"
            >
              Return to Gallery
            </button>
          </div>
        </div>
      )}

      {/* Current Segment Indicator */}
      {!loading && !error && currentSegment !== null && (
        <div className="fixed top-4 left-4 z-40 px-4 py-2 bg-black/60 backdrop-blur-sm border border-white/20 rounded-lg">
          <div className="text-white/70 text-xs">
            Segment: {currentSegment} / {manifest?.segment_count || '?'}
          </div>
        </div>
      )}

      {/* Selected POI Label */}
      {!loading && !error && selectedPOI && (
        <div className="fixed top-16 left-4 z-40 px-6 py-3 bg-blue-500/90 backdrop-blur-sm border border-blue-400/30 rounded-lg shadow-xl">
          <div className="flex items-center gap-3">
            <div>
              <div className="text-white font-semibold text-sm">{selectedPOI.name}</div>
              <div className="text-white/70 text-xs mt-0.5">
                {selectedPOI.latitude.toFixed(6)}, {selectedPOI.longitude.toFixed(6)}
              </div>
            </div>
            <button
              onClick={() => setSelectedPOI(null)}
              className="ml-2 p-1 hover:bg-white/20 rounded transition-colors"
            >
              <X size={16} className="text-white" />
            </button>
          </div>
        </div>
      )}

      {/* POI Side Panel - Shows segment-filtered POIs */}
      {!loading && !error && filteredPois.length > 0 && (
        <POISidePanel
          pois={filteredPois}  // Nearby POIs (filtered by segment)
          allPois={pois}       // ALL POIs in scene ← ADD THIS!
          onNavigate={handleNavigateToPOI}
          onClose={() => {/* Panel stays open */}}
          currentPosition={cameraPosition || undefined}
          selectedPOI={selectedPOI || undefined}
          pathNetwork={pathNetwork}
        />
      )}

      {/* Navigation Panel */}
      {showNavPanel && (
        <div 
          onClick={() => setShowNavPanel(false)}
          className="fixed inset-0 bg-black/75 backdrop-blur-sm flex items-center justify-center z-30 cursor-pointer p-5"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-3xl bg-black/85 backdrop-blur-xl rounded-3xl border border-white/10 shadow-2xl p-12 text-white cursor-default relative"
          >
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowNavPanel(false);
              }}
              className="absolute top-5 right-5 w-7 h-7 rounded-full bg-white/10 flex items-center justify-center border border-white/10 opacity-60 hover:opacity-100 transition-opacity"
            >
              <X size={12} strokeWidth={3} />
            </button>

            <h1 className="text-2xl text-center mb-10 tracking-widest opacity-90 font-light">
              Navigation
            </h1>

            <div className="text-center text-sm text-white/40 mt-8">
              Walk smoothly between POIs - No page reload!
            </div>
          </div>
        </div>
      )}
    {/* Axis Mapping Debug Panel */}
{showAxisDebug && (
  <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 z-50 bg-black/95 border-2 border-cyan-500 rounded-lg p-6 max-w-4xl shadow-2xl">
    <div className="flex justify-between items-center mb-4">
      <div>
        <h3 className="text-white font-bold text-lg">🧪 Axis Mapping Tester</h3>
        <p className="text-xs text-white/60 mt-1">Find the correct camera axis mapping</p>
      </div>
      <button
        onClick={() => setShowAxisDebug(false)}
        className="text-white/60 hover:text-white"
      >
        <X size={24} />
      </button>
    </div>
    
    <div className="text-sm text-cyan-400 mb-4 p-3 bg-cyan-500/10 rounded border border-cyan-500/30">
      <strong>How to use:</strong> Click each mapping button. Watch the scene - did camera move to the POI? 
      Check console for detailed logs. Find the one that works!
    </div>
    
    <div className="grid grid-cols-4 gap-3">
      {[
        'xyz', 'xzy', 'yxz', 'yzx', 'zxy', 'zyx',
        'x-yz', 'xz-y', 'x-z-y', '-xyz', '-xzy'
      ].map((mapping) => (
        <button
          key={mapping}
          onClick={() => {
            setAxisMapping(mapping as AxisMapping);
            testAxisMapping(mapping as AxisMapping);
          }}
          className={`px-4 py-3 rounded-lg text-base font-mono font-bold transition-all ${
            axisMapping === mapping
              ? 'bg-cyan-500 text-black scale-105'
              : 'bg-gray-700 text-white hover:bg-gray-600 hover:scale-105'
          }`}
        >
          {mapping}
        </button>
      ))}
    </div>
    
    <div className="mt-4 p-3 bg-gray-800 rounded text-sm">
      <div className="text-white/70">
        Current mapping: <span className="text-cyan-400 font-mono font-bold">{axisMapping}</span>
      </div>
      <div className="text-white/50 text-xs mt-2">
        Once you find the correct mapping, tell me which one worked!
      </div>
    </div>
  </div>
)}

{/* Debug Toggle Button */}
{!loading && !error && (
  <button
    onClick={() => setShowAxisDebug(!showAxisDebug)}
    className="fixed bottom-4 left-4 z-40 px-6 py-3 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg font-medium shadow-lg"
  >
    {showAxisDebug ? '🧪 Hide Debug' : '🧪 Show Debug'}
  </button>
)}
    </>
  );
};

export default GeoAutomationWithPOIViewer;